import { Box, Stack, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { RequestedFieldInput as RequestedFieldInputSlot } from "./share-link-fields-slot-definitions"
import { isKnownRequestedFieldType } from "./requested-field-types"
import { TextFieldInput } from "./text-field-input"

/**
 * Host for the recipient-facing input of a single requested field. Renders the shared chrome (the
 * `field.label` plus a visual-only required asterisk inside a `data-cy="requested-field-input-<id>"`
 * wrapper) and delegates the actual control to the `RequestedFieldInput` slot subtype for
 * `field.type`. Each type self-registers a plug in `share-link-fields.runtime.js`, replacing the
 * previous hardcoded `<Switch>`.
 *
 * Unknown / legacy field types have no plug; for those we render the generic `<TextFieldInput>` as the
 * slot's default child. Known types render their plug only — the host passes no default child for them
 * so the plug and a fallback never both render (slot children render alongside matched plugs).
 *
 * The `required` asterisk is purely visual — the spec does not gate saving on required fields, so we
 * surface the marker without any validation that would block auto-save. The control is bound to
 * `values.<field.id>` on the surrounding panel's editing target by each per-type plug.
 *
 * @param {object} props
 * @param {{ id: string, type: string, label: string, required?: boolean, options?: string[], currencyCode?: string }} props.field
 */
export function RequestedFieldInput({ field }) {
    if (!field?.id) return null

    return (
        <Box data-cy={`requested-field-input-${field.id}`}>
            <Stack spacing={0.5}>
                <Typography variant="subtitle2" component="label">
                    {field.label}
                    <If value={!!field.required}>
                        <Box component="span" sx={{ color: "error.main", ml: 0.5 }} aria-hidden>
                            *
                        </Box>
                    </If>
                </Typography>
                <RequestedFieldInputSlot.Slot type={field.type} field={field}>
                    <If value={!isKnownRequestedFieldType(field.type)}>
                        <TextFieldInput field={field} />
                    </If>
                </RequestedFieldInputSlot.Slot>
            </Stack>
        </Box>
    )
}
